<template>
  <el-dialog
    :model-value="visible"
    title="Costos y conceptos"
    width="500px"
    @update:model-value="$emit('update:visible', $event)"
    @close="resetForm"
  >
    <el-radio-group
      :model-value="form.Tipo"
      class="tipo-radio"
      @update:model-value="updateField('Tipo', $event)"
    >
      <el-radio value="costo">Costo</el-radio>
      <el-radio value="concepto">Concepto</el-radio>
    </el-radio-group>

    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <el-form-item prop="Descripcion">
        <custom-input
          :model-value="form.Descripcion"
          label="Descripción"
          :spanColumn="24"
          :settings="{ placeholder: 'Ej: Flete adicional' }"
          @update:model-value="updateField('Descripcion', $event)"
        />
      </el-form-item>

      <el-form-item
        v-if="!esConcepto"
        prop="Costo"
      >
        <custom-select
          :model-value="form.Costo"
          label="Costo"
          :spanColumn="24"
          fieldKey="Codigo"
          fieldLabel="Descripcion"
          :storeObject="true"
          :settings="{
            options: costos,
            placeholder: 'Seleccionar costo',
            clearable: true,
            filterable: true,
          }"
          @update:model-value="updateField('Costo', $event)"
        />
      </el-form-item>

      <el-form-item prop="Alternativa">
        <custom-select
          :model-value="form.Alternativa"
          label="Alternativa a afectar"
          :spanColumn="24"
          fieldKey="Codigo"
          fieldLabel="Descripcion"
          :storeObject="true"
          :settings="{ options: alternativas, placeholder: 'Seleccionar alternativa' }"
          @update:model-value="updateField('Alternativa', $event)"
        />
      </el-form-item>

      <template v-if="esConcepto">
        <el-form-item prop="Porcentaje">
          <custom-input-number
            :model-value="form.Porcentaje"
            label="Porcentaje (%)"
            :spanColumn="24"
            :settings="{ min: 0, placeholder: 'Porcentaje' }"
            @update:model-value="updateField('Porcentaje', $event)"
          />
        </el-form-item>

        <el-form-item
          label="Modo de cálculo"
          prop="ModoCalculo"
        >
          <el-select
            :model-value="form.ModoCalculo"
            placeholder="Seleccionar modo de cálculo"
            style="width: 100%"
            @update:model-value="updateField('ModoCalculo', $event)"
          >
            <el-option
              v-for="modo in MODOS_CALCULO"
              :key="modo.key"
              :label="modo.label"
              :value="modo.key"
            />
          </el-select>
        </el-form-item>
      </template>

      <template v-else>
        <el-form-item prop="Cantidad">
          <custom-input-number
            :model-value="form.Cantidad"
            label="Cantidad"
            :spanColumn="24"
            :settings="{ min: 0, placeholder: 'Cantidad' }"
            @update:model-value="updateField('Cantidad', $event)"
          />
        </el-form-item>

        <el-form-item prop="Precio">
          <custom-input-number
            :model-value="form.Precio"
            label="Precio / Costo"
            :spanColumn="24"
            :settings="{ min: 0, placeholder: 'Precio' }"
            @update:model-value="updateField('Precio', $event)"
          />
        </el-form-item>
      </template>
    </el-form>

    <template #footer>
      <el-button @click="$emit('update:visible', false)">Cancelar</el-button>
      <el-button
        type="primary"
        :loading="saving"
        @click="handleSave"
      >
        Guardar
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomSelect from '@/shared/components/custom_select.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  form: {
    type: Object,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  alternativas: {
    type: Array,
    default: () => [],
  },
  costos: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:visible', 'update:form', 'save']);

const formRef = ref(null);

const esConcepto = computed(() => props.form.Tipo === 'concepto');

// Mismos valores que modo_flete en Configuración General (setting_list.vue)
const MODOS_CALCULO = [
  { key: 'suma_al_costo', label: 'Se suma al costo' },
  { key: 'agrega_al_precio', label: 'Se agrega al precio' },
  { key: 'porcentaje_pv', label: 'Es % del precio de venta' },
];

const rules = computed(() => {
  const comunes = {
    Descripcion: [{ required: true, message: 'La descripción es requerida', trigger: 'blur' }],
    Alternativa: [
      { required: true, message: 'Debe seleccionar una alternativa', trigger: 'change' },
    ],
  };
  if (esConcepto.value) {
    return {
      ...comunes,
      Porcentaje: [
        {
          required: true,
          type: 'number',
          min: 0.01,
          message: 'El porcentaje es requerido y debe ser mayor a 0',
          trigger: 'blur',
        },
      ],
      ModoCalculo: [
        { required: true, message: 'Debe seleccionar un modo de cálculo', trigger: 'change' },
      ],
    };
  }
  return {
    ...comunes,
    Cantidad: [
      {
        required: true,
        type: 'number',
        min: 0,
        message: 'La cantidad es requerida',
        trigger: 'blur',
      },
    ],
    Precio: [
      {
        required: true,
        type: 'number',
        min: 0,
        message: 'El precio es requerido',
        trigger: 'blur',
      },
    ],
  };
});

function updateField(field, value) {
  emit('update:form', { ...props.form, [field]: value });
}

function resetForm() {
  formRef.value?.clearValidate();
}

async function handleSave() {
  try {
    await formRef.value.validate();
    emit('save');
  } catch (error) {
    // Error de validación
  }
}

watch(esConcepto, () => resetForm());

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      resetForm();
    }
  },
);
</script>

<style scoped>
.tipo-radio {
  display: flex;
  margin-bottom: 12px;
  justify-content: center;
}
</style>
